import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { secretNameProblem } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { Badge, Button, Card, Dialog, ErrorText, Field, Input, Spinner, Switch } from "../components/ui.tsx";

type Secret = Awaited<ReturnType<typeof load>>[number];
const load = () => unwrap(api.me.secrets.$get());

/** API keys the assistant may use (Connected apps → Developer). */
export function SecretsSection() {
  const q = useQuery({ queryKey: ["secrets"], queryFn: load });
  const [editing, setEditing] = useState<Secret | "new" | null>(null);
  return (
    <>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Secrets</h2>
          <p className="text-sm text-[var(--muted)]">
            API keys and tokens your assistant can use, for example GH_TOKEN for GitHub. Each becomes an environment variable for every command your assistant runs. Stored encrypted; only you can see them. Saving or removing one restarts your assistant (about 30 seconds).
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>Add secret</Button>
      </div>
      <Card className="divide-y divide-[var(--line)]">
        {q.isPending && <div className="grid place-items-center py-12"><Spinner /></div>}
        {q.data?.length === 0 && <p className="px-5 py-10 text-center text-sm text-[var(--muted)]">No secrets yet.</p>}
        {q.data?.map((s) => <SecretRow key={s.id} s={s} onEdit={() => setEditing(s)} />)}
      </Card>
      {editing && <SecretDialog secret={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function SecretRow({ s, onEdit }: { s: Secret; onEdit: () => void }) {
  // An admin acting as this person can replace a secret but never read it back.
  const impersonating = !!useMe().data?.user.impersonatedBy;
  const [value, setValue] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: () => unwrap(api.me.secrets[":id"].value.$get({ param: { id: s.id } })),
    onSuccess: (r) => setValue(r.value),
  });
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
      <div className="min-w-0 flex-1 basis-60">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium">{s.name}</span>
          {s.exposeToAssistant && <Badge tone="accent">assistant too</Badge>}
        </div>
        {s.description && <div className="truncate text-xs text-[var(--muted)]">{s.description}</div>}
        <div className="mt-1 break-all font-mono text-xs text-[var(--muted)]">{value ?? (s.hint ? `••••••••${s.hint}` : "••••••••")}</div>
      </div>
      <div className="flex gap-1">
        {impersonating ? null : value === null ? (
          <Button variant="ghost" className="h-8" disabled={reveal.isPending} onClick={() => reveal.mutate()}>Show</Button>
        ) : (
          <>
            <Button variant="ghost" className="h-8" onClick={() => void navigator.clipboard.writeText(value)}>Copy</Button>
            <Button variant="ghost" className="h-8" onClick={() => setValue(null)}>Hide</Button>
          </>
        )}
        <Button variant="ghost" className="h-8" onClick={onEdit}>Edit</Button>
      </div>
    </div>
  );
}

function SecretDialog({ secret, onClose }: { secret: Secret | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(secret?.name ?? "");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState(secret?.description ?? "");
  const [exposeToAssistant, setExpose] = useState(secret?.exposeToAssistant ?? false);
  const problem = name ? secretNameProblem(name.trim().toUpperCase(), exposeToAssistant) : null;
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["secrets"] });
    void qc.invalidateQueries({ queryKey: ["my-runtime"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => unwrap(api.me.secrets.$put({ json: { name, value: value || undefined, description, exposeToAssistant } })),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => unwrap(api.me.secrets[":id"].$delete({ param: { id: secret!.id } })), onSuccess: done });

  return (
    <Dialog open onClose={onClose} title={secret ? `Edit ${secret.name}` : "Add secret"}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Name" hint={problem ?? "The environment variable name, e.g. GH_TOKEN, NOTION_TOKEN."}>
          <Input required disabled={!!secret} value={name} onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))} className="font-mono" placeholder="GH_TOKEN" />
        </Field>
        <Field label="Value" hint={secret ? "Leave blank to keep the current value." : "Single line."}>
          <Input type="password" autoComplete="off" required={!secret} value={value} onChange={(e) => setValue(e.target.value)} className="font-mono" />
        </Field>
        <Field label="Description (optional)"><Input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Switch
          label="Also give it to the assistant itself"
          hint="Off: only commands and CLIs in your workspace see it (right for most keys). On: the assistant's built-in tools can use it too."
          checked={exposeToAssistant}
          onChange={setExpose}
        />
        <ErrorText error={save.error ?? remove.error} />
        <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] pt-4">
          {secret ? <Button type="button" variant="danger" onClick={() => confirm(`Delete ${secret.name}? Your assistant restarts.`) && remove.mutate()}>Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={save.isPending || !!problem}>Save</Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
